import React, { useRef, useState, useEffect } from "react";
import { PlayArrow, Pause } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";

const AudioPlayerForRouting = ({ src, compact }) => {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      setDuration(audio.duration || 0);
      setIsReady(true);
    };

    const onTimeUpdate = () => {
      if (!audio.duration) return;
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const onEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("canplaythrough", onLoaded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("canplaythrough", onLoaded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, [src]);

  const togglePlay = () => {
    if (!isReady) return;

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }

    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const sizeClasses = compact
    ? "gap-3 p-2"
    : "gap-5 p-4";
  const playBtnClasses = compact
    ? "w-7 h-7"
    : "w-8 h-8";
  const progressClasses = compact
    ? "h-1.5"
    : "h-2";
  const timeClasses = compact
    ? "text-xs"
    : "text-sm";

  return (
    <div className={`w-full max-w-3xl bg-gray-50 rounded-lg border border-gray-200 shadow-sm flex items-center ${sizeClasses}`}>
      {/* Play Button */}
      <button
        onClick={togglePlay}
        disabled={!isReady}
        className={`flex items-center justify-center ${playBtnClasses} rounded-full text-white transition shrink-0
          ${isReady ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"}
        `}
      >
        {!isReady ? (
          <CircularProgress size={compact ? 14 : 16} sx={{ color: "#fff" }} />
        ) : isPlaying ? (
          <Pause fontSize="small" />
        ) : (
          <PlayArrow fontSize="small" />
        )}
      </button>

      {/* Progress */}
      <div className="flex-1 flex items-center space-x-2 min-w-0">
        <div className={`w-full ${progressClasses} bg-gray-200 rounded-full overflow-hidden`}>
          <div
            className="h-full bg-gray-300 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Time */}
        <div className={`flex ${timeClasses} text-gray-600 whitespace-nowrap shrink-0`}>
          <span>{formatTime(audioRef.current?.currentTime)}</span>
          <span className="mx-1">/</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Hidden Audio */}
      <audio ref={audioRef} src={src} preload="auto" />
    </div>
  );
};

export default AudioPlayerForRouting;
